import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function HangmanGame({ user }) {
  const { hangmanId } = useParams();
  const navigate = useNavigate();
  
  const [activity, setActivity] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentWord, setCurrentWord] = useState(null);
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [livesRemaining, setLivesRemaining] = useState(6);
  const [timeLeft, setTimeLeft] = useState(60);
  const [score, setScore] = useState(0);
  const [wordsCompleted, setWordsCompleted] = useState([]);
  const [gameStatus, setGameStatus] = useState('loading'); // loading, playing, word_completed, game_over, won
  const [loading, setLoading] = useState(true);
  const [words, setWords] = useState([]);
  const [capsLock, setCapsLock] = useState(false); // Estado de BLOQ MAYÚS
  
  // Tracking para ranking
  const [gameStartTime, setGameStartTime] = useState(null);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [totalLivesLost, setTotalLivesLost] = useState(0);
  const [wordStartTime, setWordStartTime] = useState(null);

  // Caracteres especiales que DEBEN adivinarse (importantes para comandos Linux)
  const specialCharsToGuess = ['>', '|', '*', '+', '.', '"', '?', '~', '(', ')', '/', '\\', '-', '%', '&'];
  
  // Caracteres que se muestran automáticamente (espacios y delimitadores comunes)
  const autoRevealChars = [' ', '_', ':', ';', '[', ']', '{', '}', '<', '!', '@', '#', '$', '=', ',', "'"];

  useEffect(() => {
    startGame();
  }, []);

  useEffect(() => {
    if (gameStatus === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (gameStatus === 'playing' && timeLeft === 0) {
      handleTimeOut();
    }
  }, [timeLeft, gameStatus]);

  // Keyboard input listener
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (gameStatus !== 'playing') return;
      
      // Toggle Caps Lock with CapsLock key
      if (event.key === 'CapsLock') {
        setCapsLock(!capsLock);
        return;
      }
      
      // Get the character (case-sensitive)
      const key = event.key;
      
      // Check if it's a valid character (letters, numbers, or special chars to guess)
      const isValidLetter = /^[a-zA-ZñÑ0-9]$/.test(key);
      const isSpecialChar = specialCharsToGuess.includes(key);
      
      if (isValidLetter || isSpecialChar) {
        handleLetterClick(key);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameStatus, guessedLetters, currentWord, capsLock]);

  const startGame = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/hangman/${hangmanId}/start`, null, {
        params: { user_id: user.id }
      });
      
      setSessionId(response.data.session_id);
      setActivity(response.data.activity);
      setLivesRemaining(response.data.activity.lives);
      setTimeLeft(response.data.activity.time_per_word);
      
      // Load words
      const activityResponse = await axios.get(`${BACKEND_URL}/api/hangman/${hangmanId}`);
      setWords(activityResponse.data.words);
      setCurrentWord(activityResponse.data.words[0]);
      setGameStatus('playing');
      
      // Start tracking time
      setGameStartTime(Date.now());
      setWordStartTime(Date.now());
    } catch (error) {
      console.error('Error starting game:', error);
      alert('Error al iniciar el juego');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLetterClick = (letter) => {
    if (guessedLetters.includes(letter) || gameStatus !== 'playing') return;

    const newGuessedLetters = [...guessedLetters, letter];
    setGuessedLetters(newGuessedLetters);
    setTotalAttempts(prev => prev + 1);

    // Case-sensitive comparison
    if (!currentWord.word.includes(letter)) {
      const newLives = livesRemaining - 1;
      setTotalLivesLost(prev => prev + 1);
      setLivesRemaining(newLives);
      
      if (newLives === 0) {
        setGameStatus('game_over');
      }
    } else {
      // Check if word is complete (case-sensitive)
      const allLettersGuessed = currentWord.word.split('').every(l => 
        autoRevealChars.includes(l) || newGuessedLetters.includes(l)
      );
      
      if (allLettersGuessed) {
        handleWordCompleted(newGuessedLetters);
      }
    }
  };

  const handleWordCompleted = (letters) => {
    const bonusTime = Math.floor(timeLeft / 10) * 10; // Bonus por tiempo restante
    const wordScore = activity.points_per_word + bonusTime;
    
    setScore(score + wordScore);
    setWordsCompleted([...wordsCompleted, {
      word: currentWord.word,
      score: wordScore,
      timeUsed: activity.time_per_word - timeLeft
    }]);
    
    if (currentWordIndex + 1 < words.length) {
      setGameStatus('word_completed');
      setTimeout(() => {
        nextWord();
      }, 2000);
    } else {
      setGameStatus('won');
    }
  };

  const handleTimeOut = () => {
    const newLives = livesRemaining - 1;
    setLivesRemaining(newLives);
    
    if (newLives === 0) {
      setGameStatus('game_over');
    } else if (currentWordIndex + 1 < words.length) {
      nextWord();
    } else {
      setGameStatus('game_over');
    }
  };

  const nextWord = () => {
    const nextIndex = currentWordIndex + 1;
    setCurrentWordIndex(nextIndex);
    setCurrentWord(words[nextIndex]);
    setGuessedLetters([]);
    setTimeLeft(activity.time_per_word);
    setGameStatus('playing');
  };

  const handleFinish = async () => {
    try {
      const totalTimeSeconds = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;
      
      await axios.post(`${BACKEND_URL}/api/hangman/submit`, {
        session_id: sessionId,
        user_id: user.id,
        words_completed: wordsCompleted,
        score: score,
        // Nuevos datos para ranking
        total_time_seconds: totalTimeSeconds,
        total_attempts: totalAttempts,
        total_lives_lost: totalLivesLost,
        words_attempted: words.length
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting result:', error);
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-white mb-4"></div>
          <p className="text-xl font-bold">Cargando juego...</p>
        </div>
      </div>
    );
  }

  // Game Over Screen
  if (gameStatus === 'game_over' || gameStatus === 'won') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="text-center mb-6">
            <div className="text-7xl mb-4">
              {gameStatus === 'won' ? '🎉' : '💔'}
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {gameStatus === 'won' ? '¡Felicitaciones!' : 'Juego Terminado'}
            </h2>
            <p className="text-gray-600">
              {gameStatus === 'won' 
                ? '¡Has completado todas las palabras!' 
                : 'Te quedaste sin vidas'}
            </p>
          </div>

          <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-6 mb-6 text-white">
            <div className="text-center mb-4">
              <div className="text-5xl font-bold mb-2">{score}</div>
              <div className="text-sm opacity-90">Puntos Totales</div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold">{wordsCompleted.length}</div>
                <div className="opacity-90">Palabras Completadas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{words.length}</div>
                <div className="opacity-90">Palabras Totales</div>
              </div>
            </div>
          </div>

          {wordsCompleted.length > 0 && (
            <div className="mb-6 max-h-64 overflow-y-auto">
              <h3 className="font-bold text-gray-800 mb-3">Palabras Completadas:</h3>
              <div className="space-y-2">
                {wordsCompleted.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-100 rounded-lg p-3">
                    <span className="font-semibold text-gray-800">{item.word}</span>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>⏱️ {item.timeUsed}s</span>
                      <span className="font-bold text-pink-600">+{item.score} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleFinish}
            className="w-full px-6 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-pink-600 hover:to-purple-700 transition shadow-lg"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Playing Screen - Case-sensitive display
  const displayWord = currentWord.word.split('').map(letter => {
    // Auto-reveal special characters and spaces
    if (autoRevealChars.includes(letter)) {
      return letter;
    }
    // Show guessed letters (case-sensitive)
    return guessedLetters.includes(letter) ? letter : '_';
  }).join('');

  const wrongGuesses = guessedLetters.filter(letter => !currentWord.word.includes(letter)).length;
  const balloons = Array(livesRemaining).fill('🎈');

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-500 to-purple-600 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-t-3xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">🎯 {activity.title}</h1>
            <button
              onClick={() => {
                if (window.confirm('¿Salir del juego? Perderás tu progreso.')) {
                  navigate('/dashboard');
                }
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              ← Salir
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-purple-100 rounded-lg p-3">
              <div className="text-sm text-purple-600 mb-1">Tiempo</div>
              <div className={`text-2xl font-bold ${timeLeft < 10 ? 'text-red-600 animate-pulse' : 'text-purple-700'}`}>
                ⏱️ {timeLeft}s
              </div>
            </div>
            <div className="bg-pink-100 rounded-lg p-3">
              <div className="text-sm text-pink-600 mb-1">Puntos</div>
              <div className="text-2xl font-bold text-pink-700">⭐ {score}</div>
            </div>
            <div className="bg-green-100 rounded-lg p-3">
              <div className="text-sm text-green-600 mb-1">Progreso</div>
              <div className="text-2xl font-bold text-green-700">
                {currentWordIndex + 1}/{words.length}
              </div>
            </div>
          </div>
        </div>

        {/* Game Area */}
        <div className="bg-white shadow-lg p-8">
          {/* Lives */}
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">
              {balloons.join(' ')}
            </div>
            <div className="text-sm text-gray-600">{livesRemaining} vidas restantes</div>
          </div>

          {/* Hint */}
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💡</span>
              <div>
                <div className="font-semibold text-yellow-800 mb-1">Pista:</div>
                <div className="text-gray-700">{currentWord.hint}</div>
                {currentWord.category && (
                  <div className="mt-2">
                    <span className="px-3 py-1 bg-yellow-200 text-yellow-800 text-xs font-semibold rounded-full">
                      {currentWord.category}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Word Display */}
          <div className="text-center mb-8">
            <div className="text-4xl font-bold text-gray-800 tracking-wide mb-2 font-mono bg-gray-100 rounded-lg p-6">
              {displayWord}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              💡 Usa tu teclado físico o haz clic en los botones | 🔒 Presiona CapsLock para BLOQ MAYÚS
            </p>
            {gameStatus === 'word_completed' && (
              <div className="text-2xl text-green-600 font-bold animate-bounce">
                ✓ ¡Correcto!
              </div>
            )}
          </div>

          {/* Wrong Letters */}
          {wrongGuesses > 0 && (
            <div className="text-center mb-6">
              <div className="text-sm text-red-600 mb-2">Caracteres incorrectos:</div>
              <div className="flex justify-center gap-2 flex-wrap">
                {guessedLetters.filter(l => !currentWord.word.includes(l)).map((letter, i) => (
                  <span key={i} className="px-3 py-1 bg-red-100 text-red-700 font-bold rounded-lg font-mono">
                    {letter === ' ' ? '␣' : letter}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Keyboard Layout */}
          <div className="space-y-4">
            {/* BLOQ MAYÚS Button */}
            <div className="flex justify-center mb-2">
              <button
                onClick={() => setCapsLock(!capsLock)}
                className={`px-6 py-3 rounded-lg font-bold text-sm transition-all ${
                  capsLock 
                    ? 'bg-yellow-500 text-white shadow-lg ring-2 ring-yellow-300' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                {capsLock ? '🔒 BLOQ MAYÚS ACTIVADO' : '🔓 BLOQ MAYÚS'}
              </button>
            </div>

            {/* Letters (toggleable with Caps Lock) */}
            <div className="mb-4">
              <div className="text-center text-sm text-gray-600 mb-2 font-semibold">
                {capsLock ? 'Letras (MAYÚSCULAS)' : 'Letras (minúsculas)'}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {(capsLock 
                  ? 'ABCDEFGHIJKLMNÑOPQRSTUVWXYZ' 
                  : 'abcdefghijklmnñopqrstuvwxyz'
                ).split('').map((letter) => {
                  const isGuessed = guessedLetters.includes(letter);
                  const isCorrect = isGuessed && currentWord.word.includes(letter);
                  const isWrong = isGuessed && !currentWord.word.includes(letter);
                  
                  return (
                    <button
                      key={letter}
                      onClick={() => handleLetterClick(letter)}
                      disabled={isGuessed || gameStatus !== 'playing'}
                      className={`
                        w-10 h-10 rounded-lg font-bold text-sm transition-all transform hover:scale-110 font-mono
                        ${isCorrect ? 'bg-green-500 text-white' : ''}
                        ${isWrong ? 'bg-red-500 text-white' : ''}
                        ${!isGuessed && gameStatus === 'playing' ? 'bg-purple-200 text-purple-700 hover:bg-purple-300 cursor-pointer' : ''}
                        ${isGuessed || gameStatus !== 'playing' ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Numbers */}
            <div className="mb-4">
              <div className="text-center text-sm text-gray-600 mb-2 font-semibold">Números</div>
              <div className="flex flex-wrap justify-center gap-2">
                {'0123456789'.split('').map((num) => {
                  const isGuessed = guessedLetters.includes(num);
                  const isCorrect = isGuessed && currentWord.word.includes(num);
                  const isWrong = isGuessed && !currentWord.word.includes(num);
                  
                  return (
                    <button
                      key={num}
                      onClick={() => handleLetterClick(num)}
                      disabled={isGuessed || gameStatus !== 'playing'}
                      className={`
                        w-10 h-10 rounded-lg font-bold text-sm transition-all transform hover:scale-110 font-mono
                        ${isCorrect ? 'bg-green-500 text-white' : ''}
                        ${isWrong ? 'bg-red-500 text-white' : ''}
                        ${!isGuessed && gameStatus === 'playing' ? 'bg-blue-200 text-blue-700 hover:bg-blue-300 cursor-pointer' : ''}
                        ${isGuessed || gameStatus !== 'playing' ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Special Characters for Commands */}
            <div className="mb-4">
              <div className="text-center text-sm text-gray-600 mb-2 font-semibold">
                Caracteres Especiales (comandos Linux)
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {specialCharsToGuess.map((char) => {
                  const isGuessed = guessedLetters.includes(char);
                  const isCorrect = isGuessed && currentWord.word.includes(char);
                  const isWrong = isGuessed && !currentWord.word.includes(char);
                  
                  return (
                    <button
                      key={char}
                      onClick={() => handleLetterClick(char)}
                      disabled={isGuessed || gameStatus !== 'playing'}
                      className={`
                        w-10 h-10 rounded-lg font-bold text-lg transition-all transform hover:scale-110 font-mono
                        ${isCorrect ? 'bg-green-500 text-white' : ''}
                        ${isWrong ? 'bg-red-500 text-white' : ''}
                        ${!isGuessed && gameStatus === 'playing' ? 'bg-orange-200 text-orange-700 hover:bg-orange-300 cursor-pointer' : ''}
                        ${isGuessed || gameStatus !== 'playing' ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                      title={char}
                    >
                      {char}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>


          {/* Info about special characters */}
          <div className="text-center text-sm text-gray-500 bg-blue-50 rounded-lg p-3 mt-4">
            <div className="font-semibold mb-1">ℹ️ Instrucciones:</div>
            <div>• Usa BLOQ MAYÚS para alternar entre mayúsculas y minúsculas</div>
            <div>• Los caracteres naranjas (>, |, *, +, etc.) deben adivinarse</div>
            <div>• Espacios y algunos símbolos (_ : [ ] etc.) se muestran automáticamente</div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white rounded-b-3xl shadow-lg p-4 text-center text-sm text-gray-600">
          Palabra {currentWordIndex + 1} de {words.length}
        </div>
      </div>
    </div>
  );
}

export default HangmanGame;
